import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiUserGroup,
  HiShieldCheck,
  HiChartBar,
  HiAcademicCap,
  HiClipboardDocumentList,
  HiUsers,
  HiSparkles,
  HiArrowTrendingUp,
  HiArrowTrendingDown,
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiMagnifyingGlass,
  HiFunnel,
  HiArrowPath,
  HiEye,
  HiPencil,
  HiTrash,
  HiChevronDown,
} from 'react-icons/hi2';
import { firestore } from '../../database/firebase';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import useAuth from '../../hooks/useAuth';
import { toast } from '../../utils/toast';
import AdminManagementTable from '../../pages/users/superadmin/AdminManagementTable';

const StatCard = ({ title, value, change, icon: Icon, gradient, trend = 'up' }) => (
  <motion.div
    whileHover={{ y: -4, scale: 1.02 }}
    transition={{ type: 'spring', stiffness: 300 }}
    className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 transition-all hover:shadow-xl"
  >
    <div className="relative z-10">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-600">{title}</p>
          <p className="mt-2 text-4xl font-bold text-black">{value.toLocaleString()}</p>
          {change && (
            <div className="mt-3 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                  trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}
              >
                {trend === 'up' ? (
                  <HiArrowTrendingUp className="h-3 w-3" />
                ) : (
                  <HiArrowTrendingDown className="h-3 w-3" />
                )}
                {change}
              </span>
              <span className="text-xs text-gray-500">vs last month</span>
            </div>
          )}
        </div>
        <motion.div
          whileHover={{ scale: 1.1, rotate: 5 }}
          className={`flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-white shadow-lg`}
        >
          <Icon className="h-7 w-7" />
        </motion.div>
      </div>
    </div>
    <div
      className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 transition-opacity group-hover:opacity-5`}
    />
  </motion.div>
);

const QuickActionCard = ({ title, description, icon: Icon, onClick, color = 'blue' }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="group w-full rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-all hover:border-blue-300 hover:shadow-lg"
  >
    <div className="flex items-start gap-4">
      <div
        className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-${color}-100`}
      >
        <Icon className={`h-6 w-6 text-${color}-600`} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-bold text-black group-hover:text-blue-600">{title}</h3>
        <p className="mt-1 text-sm text-gray-600">{description}</p>
      </div>
    </div>
  </motion.button>
);

const UserCollectionTable = ({ collectionName, users, loading }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = search
        ? user.email?.toLowerCase().includes(search.toLowerCase()) ||
          user.firstname?.toLowerCase().includes(search.toLowerCase()) ||
          user.lastname?.toLowerCase().includes(search.toLowerCase()) ||
          user.name?.toLowerCase().includes(search.toLowerCase())
        : true;

      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [users, search, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-8 w-8 rounded-full border-4 border-blue-600 border-t-transparent"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <HiMagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${collectionName}...`}
            className="w-full rounded-lg border-2 border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm font-medium text-black transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
                  Phone
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <HiUsers className="h-12 w-12 text-gray-300" />
                      <p className="text-sm font-medium text-gray-500">
                        {search
                          ? 'No users found matching your search'
                          : 'No users in this collection'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <motion.tr
                    key={user.id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-semibold text-white">
                          {(user.firstname?.[0] || user.name?.[0] || '?').toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-black">
                            {user.name ||
                              `${user.firstname || ''} ${user.lastname || ''}`.trim() ||
                              'N/A'}
                          </p>
                          {user.organization && (
                            <p className="text-xs text-gray-500">{user.organization}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm font-medium text-gray-700">{user.email || 'N/A'}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-gray-600">{user.phone || 'N/A'}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          user.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : user.status === 'inactive'
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {user.status === 'active' && <HiCheckCircle className="h-3 w-3" />}
                        {user.status === 'inactive' && <HiXCircle className="h-3 w-3" />}
                        {user.status?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="rounded-lg bg-blue-50 p-2 text-blue-600 transition-colors hover:bg-blue-100"
                          title="View Details"
                        >
                          <HiEye className="h-4 w-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="rounded-lg bg-gray-50 p-2 text-gray-600 transition-colors hover:bg-gray-100"
                          title="Edit"
                        >
                          <HiPencil className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default function SuperAdminCRMEnhanced() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalAdmins: 0,
    totalAlumni: 0,
    activeCoaches: 0,
    activeClients: 0,
    pendingApplications: 0,
    activeModerators: 0,
    totalUsers: 0,
  });

  const [collections, setCollections] = useState({
    admins: [],
    alumni: [],
    coaches: [],
    clients: [],
    moderators: [],
    superAdmins: [],
  });

  const [loading, setLoading] = useState({
    stats: true,
    admins: false,
    alumni: false,
    coaches: false,
    clients: false,
    moderators: false,
    superAdmins: false,
  });

  // Real-time stats tracking
  useEffect(() => {
    if (!user) return;

    const unsubscribeFunctions = [];

    // Admins
    const adminsUnsubscribe = onSnapshot(
      collection(firestore, 'admin'),
      snapshot => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setStats(prev => ({ ...prev, totalAdmins: data.length }));
        setCollections(prev => ({ ...prev, admins: data }));
      },
      error => toast.error('Failed to fetch admins')
    );
    unsubscribeFunctions.push(adminsUnsubscribe);

    // Alumni
    const alumniUnsubscribe = onSnapshot(
      collection(firestore, 'alumni'),
      snapshot => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setStats(prev => ({ ...prev, totalAlumni: data.length }));
        setCollections(prev => ({ ...prev, alumni: data }));
      },
      error => toast.error('Failed to fetch alumni')
    );
    unsubscribeFunctions.push(alumniUnsubscribe);

    // Coaches
    const coachesUnsubscribe = onSnapshot(
      collection(firestore, 'coaches'),
      snapshot => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const active = data.filter(d => d.status === 'active').length;
        setStats(prev => ({ ...prev, activeCoaches: active }));
        setCollections(prev => ({ ...prev, coaches: data }));
      },
      error => toast.error('Failed to fetch coaches')
    );
    unsubscribeFunctions.push(coachesUnsubscribe);

    // Clients
    const clientsUnsubscribe = onSnapshot(
      collection(firestore, 'clients'),
      snapshot => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const active = data.filter(d => d.status === 'active').length;
        setStats(prev => ({ ...prev, activeClients: active }));
        setCollections(prev => ({ ...prev, clients: data }));
      },
      error => toast.error('Failed to fetch clients')
    );
    unsubscribeFunctions.push(clientsUnsubscribe);

    // Moderators
    const moderatorsUnsubscribe = onSnapshot(
      collection(firestore, 'moderator'),
      snapshot => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const active = data.filter(d => d.status === 'active').length;
        setStats(prev => ({ ...prev, activeModerators: active }));
        setCollections(prev => ({ ...prev, moderators: data }));
      },
      error => toast.error('Failed to fetch moderators')
    );
    unsubscribeFunctions.push(moderatorsUnsubscribe);

    // Super Admins
    const superAdminsUnsubscribe = onSnapshot(
      collection(firestore, 's_admin'),
      snapshot => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCollections(prev => ({ ...prev, superAdmins: data }));
      },
      error => toast.error('Failed to fetch super admins')
    );
    unsubscribeFunctions.push(superAdminsUnsubscribe);

    // Applications
    const applicationsUnsubscribe = onSnapshot(
      query(collection(firestore, 'applications'), where('status', '==', 'pending')),
      snapshot => {
        setStats(prev => ({ ...prev, pendingApplications: snapshot.size }));
      },
      error => toast.error('Failed to fetch applications')
    );
    unsubscribeFunctions.push(applicationsUnsubscribe);

    setLoading(prev => ({ ...prev, stats: false }));

    return () => {
      unsubscribeFunctions.forEach(unsub => unsub());
    };
  }, [user]);

  // Calculate total users
  useEffect(() => {
    const total =
      stats.totalAdmins +
      stats.totalAlumni +
      collections.coaches.length +
      collections.clients.length +
      collections.moderators.length +
      collections.superAdmins.length;
    setStats(prev => ({ ...prev, totalUsers: total }));
  }, [stats.totalAdmins, stats.totalAlumni, collections]);

  const tabs = [
    { id: 'overview', name: 'Dashboard', icon: HiChartBar, color: 'blue' },
    { id: 'admins', name: 'Admins', icon: HiShieldCheck, color: 'blue', count: stats.totalAdmins },
    {
      id: 'super-admins',
      name: 'Super Admins',
      icon: HiSparkles,
      color: 'purple',
      count: collections.superAdmins.length,
    },
    {
      id: 'moderators',
      name: 'Business Advisors',
      icon: HiClipboardDocumentList,
      color: 'orange',
      count: stats.activeModerators,
    },
    {
      id: 'coaches',
      name: 'Coaches',
      icon: HiAcademicCap,
      color: 'green',
      count: stats.activeCoaches,
    },
    {
      id: 'clients',
      name: 'Clients',
      icon: HiUserGroup,
      color: 'indigo',
      count: stats.activeClients,
    },
    { id: 'alumni', name: 'Alumni', icon: HiUsers, color: 'teal', count: stats.totalAlumni },
  ];

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      change: '+12%',
      icon: HiUsers,
      gradient: 'from-blue-600 to-blue-700',
      trend: 'up',
    },
    {
      title: 'Active Clients',
      value: stats.activeClients,
      change: '+8%',
      icon: HiUserGroup,
      gradient: 'from-indigo-600 to-indigo-700',
      trend: 'up',
    },
    {
      title: 'Active Coaches',
      value: stats.activeCoaches,
      change: '+5%',
      icon: HiAcademicCap,
      gradient: 'from-green-600 to-green-700',
      trend: 'up',
    },
    {
      title: 'Alumni',
      value: stats.totalAlumni,
      change: '+15%',
      icon: HiUsers,
      gradient: 'from-teal-600 to-teal-700',
      trend: 'up',
    },
    {
      title: 'Pending Applications',
      value: stats.pendingApplications,
      change: '-3%',
      icon: HiClipboardDocumentList,
      gradient: 'from-orange-600 to-orange-700',
      trend: 'down',
    },
    {
      title: 'Moderators',
      value: stats.activeModerators,
      change: '+2%',
      icon: HiShieldCheck,
      gradient: 'from-purple-600 to-purple-700',
      trend: 'up',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', delay: 0.1 }}
                className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-xl shadow-blue-500/30"
              >
                <HiSparkles className="h-7 w-7" />
              </motion.div>
              <div>
                <h1 className="text-3xl font-bold text-black">Super Admin CRM</h1>
                <p className="mt-1 text-base text-gray-600">
                  Complete platform oversight and user management
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
            >
              <HiArrowPath className="h-4 w-4" />
              Refresh
            </motion.button>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex gap-2 overflow-x-auto rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
            {tabs.map((tab, index) => (
              <motion.button
                key={tab.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => setActiveTab(tab.id)}
                className={`group relative flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <tab.icon
                  className={`h-4 w-4 ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-110'} transition-transform`}
                />
                <span>{tab.name}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span
                    className={`ml-1 rounded-full px-2 py-0.5 text-xs font-bold ${
                      activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTabIndicator"
                    className="absolute inset-0 rounded-lg border-2 border-blue-400"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {statCards.map((card, index) => (
                    <StatCard key={card.title} {...card} />
                  ))}
                </div>

                {/* Quick Actions */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                  <h2 className="mb-6 flex items-center gap-2 text-xl font-bold text-black">
                    <HiSparkles className="h-6 w-6 text-blue-600" />
                    Quick Actions
                  </h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <QuickActionCard
                      title="View All Admins"
                      description="Manage admin accounts and permissions"
                      icon={HiShieldCheck}
                      onClick={() => setActiveTab('admins')}
                      color="blue"
                    />
                    <QuickActionCard
                      title="View Super Admins"
                      description="Highest level administrative access"
                      icon={HiSparkles}
                      onClick={() => setActiveTab('super-admins')}
                      color="purple"
                    />
                    <QuickActionCard
                      title="Manage Coaches"
                      description="View and manage active coaches"
                      icon={HiAcademicCap}
                      onClick={() => setActiveTab('coaches')}
                      color="green"
                    />
                    <QuickActionCard
                      title="Manage Clients"
                      description="View all client accounts"
                      icon={HiUserGroup}
                      onClick={() => setActiveTab('clients')}
                      color="indigo"
                    />
                    <QuickActionCard
                      title="View Alumni"
                      description="Alumni network management"
                      icon={HiUsers}
                      onClick={() => setActiveTab('alumni')}
                      color="teal"
                    />
                    <QuickActionCard
                      title="Manage Business Advisors"
                      description="Business advisors"
                      icon={HiClipboardDocumentList}
                      onClick={() => setActiveTab('moderators')}
                      color="orange"
                    />
                  </div>
                </div>

                {/* System Overview */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                  <h2 className="mb-4 text-xl font-bold text-black">System Overview</h2>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <h3 className="mb-2 font-semibold text-blue-900">Platform Health</h3>
                      <p className="text-sm text-blue-800">
                        All systems operational. {stats.totalUsers} total users across 6
                        collections.
                      </p>
                    </div>
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <h3 className="mb-2 font-semibold text-green-900">Real-Time Monitoring</h3>
                      <p className="text-sm text-green-800">
                        Live data synchronization active. Statistics update automatically.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'admins' && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-black">Admin Management</h2>
                    <p className="text-sm text-gray-600">Manage all administrator accounts</p>
                  </div>
                  <span className="rounded-full bg-blue-100 px-4 py-2 text-sm font-bold text-blue-700">
                    {stats.totalAdmins} Admins
                  </span>
                </div>
                <AdminManagementTable />
              </div>
            )}

            {activeTab === 'super-admins' && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/30">
                      <HiSparkles className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-black">Super Admins</h2>
                      <p className="text-sm text-gray-600">Highest level administrative access</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-purple-100 px-4 py-2 text-sm font-bold text-purple-700">
                    {collections.superAdmins.length} Users
                  </span>
                </div>
                <UserCollectionTable
                  collectionName="Super Admins"
                  users={collections.superAdmins}
                  loading={loading.superAdmins}
                />
              </div>
            )}

            {activeTab === 'moderators' && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-600 to-orange-700 text-white shadow-lg shadow-orange-500/30">
                      <HiClipboardDocumentList className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-black">Business Advisors</h2>
                      <p className="text-sm text-gray-600">Manage business advisor accounts</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-orange-100 px-4 py-2 text-sm font-bold text-orange-700">
                    {collections.moderators.length} Users
                  </span>
                </div>
                <UserCollectionTable
                  collectionName="Business Advisors"
                  users={collections.moderators}
                  loading={loading.moderators}
                />
              </div>
            )}

            {activeTab === 'coaches' && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-green-600 to-green-700 text-white shadow-lg shadow-green-500/30">
                      <HiAcademicCap className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-black">Coaches & Mentors</h2>
                      <p className="text-sm text-gray-600">Manage coaching staff</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-green-100 px-4 py-2 text-sm font-bold text-green-700">
                    {collections.coaches.length} Coaches
                  </span>
                </div>
                <UserCollectionTable
                  collectionName="Coaches"
                  users={collections.coaches}
                  loading={loading.coaches}
                />
              </div>
            )}

            {activeTab === 'clients' && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-indigo-700 text-white shadow-lg shadow-indigo-500/30">
                      <HiUserGroup className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-black">Clients & Entrepreneurs</h2>
                      <p className="text-sm text-gray-600">Manage client accounts</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-indigo-100 px-4 py-2 text-sm font-bold text-indigo-700">
                    {collections.clients.length} Clients
                  </span>
                </div>
                <UserCollectionTable
                  collectionName="Clients"
                  users={collections.clients}
                  loading={loading.clients}
                />
              </div>
            )}

            {activeTab === 'alumni' && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg ring-1 ring-black/5 sm:p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-600 to-teal-700 text-white shadow-lg shadow-teal-500/30">
                      <HiUsers className="h-6 w-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-black">Alumni Network</h2>
                      <p className="text-sm text-gray-600">Manage alumni members</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-teal-100 px-4 py-2 text-sm font-bold text-teal-700">
                    {collections.alumni.length} Alumni
                  </span>
                </div>
                <UserCollectionTable
                  collectionName="Alumni"
                  users={collections.alumni}
                  loading={loading.alumni}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
